import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sortObjectOptions } from '@/utils/sortOptions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditSubscriptionPage = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }
    fetchData();
  }, [subscriptionId, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all subscriptions to find the one we need
      const subsRes = await axios.get(`${API}/subscriptions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sub = subsRes.data.subscriptions.find(s => s.subscription_id === subscriptionId);
      if (!sub) {
        toast.error('Subscription not found');
        navigate('/subscriptions/manage');
        return;
      }
      setSubscription(sub);
      setSelectedPlan(sub.plan_id);
      
      // Fetch plans
      const plansRes = await axios.get(`${API}/subscriptions/plans`);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/subscriptions/${subscriptionId}/edit`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            plan_id: selectedPlan
          }
        }
      );
      
      setSuccess(true);
      toast.success('Subscription updated successfully');
      
      setTimeout(() => {
        navigate('/subscriptions/manage');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  if (!subscription) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-[22px] font-bold mb-2" style={{ color: '#0056B3' }}>Subscription Updated!</h2>
          <p className="text-gray-600 mb-6">The subscription has been updated successfully.</p>
          <p className="text-sm text-gray-500">Redirecting...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Edit Subscription</h1>
            <Button onClick={() => navigate('/subscriptions/manage')} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8">
          <h2 className="text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>
            Update Subscription for {subscription.hospital_name}
          </h2>

          <div className="space-y-6">
            {/* Hospital Info (Read-only) */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Hospital</p>
              <p className="font-semibold">{subscription.hospital_name}</p>
            </div>

            {/* Current Plan Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Current Plan</p>
              <p className="font-semibold">{subscription.plan_name}</p>
              <p className="text-sm text-gray-600 mt-2">
                Licenses: {subscription.active_staff_count} / {subscription.total_licenses === 999999 ? '∞' : subscription.total_licenses}
              </p>
            </div>

            {/* Plan Selection */}
            <div>
              <Label>Select New Plan *</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(plans, 'plan_name').map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.plan_name} - {plan.total_licenses === 999999 ? 'Unlimited' : plan.total_licenses} licenses
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-4 pt-4">
              <Button
                onClick={() => navigate('/subscriptions/manage')}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-[#0056B3] hover:bg-[#0056B3]/90"
                disabled={loading || selectedPlan === subscription.plan_id}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update Subscription'
                )}
              </Button>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default EditSubscriptionPage;
